import { ErrorService } from '@zaoblako/web/core/services/error.service';

import {
  Component,
  OnInit,
  ChangeDetectorRef
} from '@angular/core';
import { Recorder } from 'recorder-client';
import { Mp3Encoder } from '@breezystack/lamejs';
import { Subject } from 'rxjs';

/** Abstract component which implements audio recorder */
@Component({
  selector: 'crm-audio-recorder',
  template: ''
})
export abstract class AudioRecorderComponent implements OnInit {
  // Recording settings
  /** Audio sample rate */
  public audioSampleRate: number = 44100;
  /** Audio chunk size */
  public audioChunkSize: number = 1152;
  /** Audio bit rate (in kbps) */
  public audioBitRate: number = 128;

  // State props
  /** Microphone permission state */
  public permission: PermissionState = 'denied';
  /** Is recording in progress */
  public isRecording: boolean;
  /** Recorder instance */
  private recorder: Recorder;

  // Subscriptions
  protected readonly onMp3Data$ = new Subject<BlobPart[]>();

  public constructor(
    protected readonly errorService: ErrorService,
    protected readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    // Initialize recorder
    this.recorder = new Recorder({
      sampleRate: this.audioSampleRate,
      chunkSize: this.audioChunkSize
    });
    this.recorder.onstart = () => this.onStart();
    this.recorder.onstop = (raw) => this.onStop(raw);

    navigator.permissions
      .query({ name: 'microphone' as any })
      .then((value) => {
        this.permission = value.state;
        this.cdRef.markForCheck();
      });
  }

  /**
   * Start (call {@link AudioRecorderComponent.startRecording})
   * or stop (call {@link AudioRecorderComponent.stopRecording}) recording
   * depends on state ({@link AudioRecorderComponent.isRecording} value)
   */
  public onRecorderSwitch(): void {
    if (this.isRecording) {
      this.stopRecording();
    } else {
      this.startRecording();
    }
  }

  /** Start audio recording. Request permission to micriphone if required. */
  public async startRecording() {
    switch (this.permission) {
      case 'denied':
        this.errorService.addCustomError('Невозможно сделать аудиозапись', 'Вы запретили доступ к микрофону');
        return;
      case 'prompt': {
        const granted = await this.requestPermission();
        if (!granted) {
          return;
        }
        break;
      }
    }
    this.recorder.start();
  }

  /** Stop audio recording. Will trigger {@link AudioRecorderComponent.onMp3Data} callback with recording. */
  public stopRecording(): void {
    this.recorder.stop();
  }

  /** Request microphone permission */
  private async requestPermission(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Stop the tracks immediately if you only wanted to check permission
      for (const track of stream.getTracks()) {
        track.stop();
      }

      return true;
    } catch (err) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        this.errorService.addCustomError('Невозможно сделать аудиозапись', 'Вы запретили доступ к микрофону');
        this.permission = 'denied';
        this.cdRef.markForCheck();

        return false;
      } else {
        this.errorService.addCustomError('Невозможно сделать аудиозапись', `Ошибка оборудования: ${String(err)}`);
        return false;
      }
    }
  }

  private onStart(): void {
    this.isRecording = true;
    this.cdRef.markForCheck();
  }

  private onStop(raw: Int16Array): void {
    // Encode to MP3
    const mp3Data: BlobPart[] = [];
    const encoder = new Mp3Encoder(1, this.audioSampleRate, this.audioBitRate);
    for (let i = 0; i < raw.length; i += this.audioChunkSize) {
      const sampleChunk = raw.subarray(i, i + this.audioChunkSize);
      const mp3buf = encoder.encodeBuffer(sampleChunk);
      if (mp3buf.length > 0) {
        mp3Data.push(mp3buf);
      }
    }
    const mp3buf = encoder.flush();
    if (mp3buf.length > 0) {
      mp3Data.push(new Int8Array(mp3buf));
    }

    // Switch recording state
    this.isRecording = true;
    this.cdRef.markForCheck();

    // Trigger MP3 data handler
    this.onMp3Data$.next(mp3Data);
  }
}
